import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { useUser } from '../components/UserContext';
import { useComisionLedger } from '../contexts/DataContext';
import { usePulsoData } from '../hooks/usePulsoData';
import { getFianzaStatus } from '../utils/convenioCompliance';
import { getPrimaAmount } from '../utils/calculations';
import { facturasPorPagar } from '../utils/saldosCliente';
import { BitacoraTimeline } from '../components/BitacoraTimeline';
import {
  UserGroupIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);

const TONE_STYLES = {
  red: {
    card: 'bg-red-50 border-red-200 hover:border-red-300',
    value: 'text-red-600',
    detail: 'text-red-600',
  },
  amber: {
    card: 'bg-amber-50 border-amber-200 hover:border-amber-300',
    value: 'text-amber-600',
    detail: 'text-amber-700',
  },
  neutral: {
    card: 'bg-white border-azul-surelia/20 hover:border-azul-surelia/40',
    value: 'text-azul-surelia',
    detail: 'text-azul-surelia/60',
  },
};

const Home = () => {
  const navigate = useNavigate();
  const { userData, isOwner, isOperator } = useUser();
  const {
    clientsList,
    contractsList,
    expirationCounts,
    endososPendientesCounts,
    despuesTerminoCounts,
    pipelineCounts,
    clientCounts,
    loading,
  } = usePulsoData();
  const { movements } = useComisionLedger();

  const year = new Date().getFullYear();

  // KPIs — vigente-only, so terminal fianzas (cancelada/anulada/sustituida)
  // no longer inflate the headline numbers.
  const kpis = useMemo(() => {
    let fianzasVigentes = 0;
    let primaVigente = 0;
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        if (getFianzaStatus(fianza).code !== 'vigente') return;
        fianzasVigentes += 1;
        primaVigente += getPrimaAmount(fianza) || 0;
      });
    });
    return {
      clientes: clientsList.length,
      contratos: contractsList.length,
      fianzasVigentes,
      primaVigente,
    };
  }, [clientsList, contractsList]);

  const facturas = useMemo(() => facturasPorPagar(contractsList, Date.now()), [contractsList]);
  const facturasRiesgo = useMemo(() => facturas.filter((f) => f.enRiesgoBuro).length, [facturas]);

  // Same bucket rules as ClientComisiones — the two must agree or Inicio and
  // Comisiones report different saldos for the same ledger.
  const comisionSaldos = useMemo(() => {
    const totals = { cobrable: 0, porExigir: 0, adeudo: 0 };
    movements.forEach((m) => {
      const monto = Number(m.comisionMonto) || 0;
      if (m.estadoCobro === 'pendiente' && monto > 0) {
        if (m.facturaPagada === true) totals.cobrable += monto;
        else totals.porExigir += monto;
      } else if (m.estadoCobro === 'adeudo') {
        totals.adeudo += Math.abs(monto);
      }
    });
    return totals;
  }, [movements]);

  const [metas, setMetas] = useState(null);
  useEffect(() => {
    if (!isOwner || !userData?.org) return undefined;
    const unsub = onSnapshot(
      doc(db, 'orgs', userData.org, 'metas', String(year)),
      (snap) => setMetas(snap.data()?.metas || null),
      () => setMetas(null)
    );
    return unsub;
  }, [isOwner, userData?.org, year]);

  const metasRows = useMemo(() => {
    if (!metas) return [];
    const primaPorAfianzadora = new Map();
    movements.forEach((m) => {
      if (!m.afianzadora || !m.fecha) return;
      if (new Date(m.fecha).getFullYear() !== year) return;
      const prev = primaPorAfianzadora.get(m.afianzadora) || 0;
      primaPorAfianzadora.set(m.afianzadora, prev + (Number(m.prima) || 0));
    });
    return Object.entries(metas)
      .map(([afianzadora, meta]) => ({
        afianzadora,
        meta: Number(meta) || 0,
        vendida: primaPorAfianzadora.get(afianzadora) || 0,
      }))
      .filter((r) => r.meta > 0)
      .sort((a, b) => b.meta - a.meta)
      .slice(0, 3);
  }, [metas, movements, year]);

  const puedeVerReportes = isOwner || isOperator;

  const atencion = useMemo(() => {
    const cards = [];

    if (endososPendientesCounts.total > 0) {
      const detailParts = [];
      if (endososPendientesCounts.vencidos > 0) detailParts.push(`${endososPendientesCounts.vencidos} vencidos`);
      if (endososPendientesCounts.urgentes > 0) detailParts.push(`${endososPendientesCounts.urgentes} urgentes`);
      cards.push({
        key: 'endosos',
        label: 'Endosos pendientes',
        value: endososPendientesCounts.total,
        detail: detailParts.join(' · ') || null,
        tone: endososPendientesCounts.needAttention > 0 ? 'red' : 'amber',
        to: '/app/pulso?tab=endosos_pendientes',
      });
    }

    if (expirationCounts.under30 > 0 || expirationCounts.vencidas > 0) {
      cards.push({
        key: 'vencimientos',
        label: 'Vencen en 30 días',
        value: expirationCounts.under30,
        detail: expirationCounts.vencidas > 0 ? `${expirationCounts.vencidas} ya vencidas` : null,
        tone: expirationCounts.vencidas > 0 ? 'red' : 'amber',
        to: '/app/pulso?tab=vencimientos',
      });
    }

    if (facturas.length > 0) {
      cards.push({
        key: 'facturas',
        label: 'Facturas por pagar',
        value: facturas.length,
        detail: facturasRiesgo > 0 ? `${facturasRiesgo} en riesgo de buró` : null,
        tone: facturasRiesgo > 0 ? 'red' : 'amber',
        // Cobranza de primas is the surface that answers this tile: same
        // facturasPorPagar rows, aged, and clicking one opens the fianza where
        // the factura gets marked pagada. Same gate as Reportes — cobranza is
        // one of the reportes an operator may open.
        to: puedeVerReportes
          ? `/app/reportes?reporte=cobranza-primas${facturasRiesgo > 0 ? '&filtro=riesgo' : ''}`
          : '/app/fianzas',
      });
    }

    if (despuesTerminoCounts.needsAttention > 0) {
      cards.push({
        key: 'despues_termino',
        label: 'Después del término',
        value: despuesTerminoCounts.needsAttention,
        detail: `${despuesTerminoCounts.total} diferidas en total`,
        tone: 'amber',
        to: '/app/pulso?tab=despues_termino',
      });
    }

    if (pipelineCounts.pendientes > 0) {
      cards.push({
        key: 'pipeline',
        label: 'Requeridas sin iniciar',
        value: pipelineCounts.pendientes,
        detail: null,
        tone: 'neutral',
        to: '/app/pulso?tab=pipeline',
      });
    }

    const expedientesPendientes = clientCounts.vencidos + clientCounts.incompletos;
    if (expedientesPendientes > 0) {
      cards.push({
        key: 'expedientes',
        label: 'Expedientes con pendientes',
        value: expedientesPendientes,
        detail: clientCounts.vencidos > 0 ? `${clientCounts.vencidos} con documentos vencidos` : null,
        tone: clientCounts.vencidos > 0 ? 'red' : 'amber',
        to: '/app/pulso?tab=clientes',
      });
    }

    return cards;
  }, [
    endososPendientesCounts,
    expirationCounts,
    facturas.length,
    facturasRiesgo,
    despuesTerminoCounts,
    pipelineCounts.pendientes,
    clientCounts,
    puedeVerReportes,
  ]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días';
    if (hour < 19) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  const KpiCard = ({ icon: Icon, label, value, onClick }) => (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg shadow-lg border border-azul-surelia/20 p-4 sm:p-5 hover:shadow-xl transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:scale-[1.02]' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg bg-azul-surelia shrink-0">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <Heading level={2} className="text-2xl sm:text-3xl font-bold text-azul-surelia tabular-nums">
          {value}
        </Heading>
      </div>
      <Text className="text-xs sm:text-sm text-azul-surelia/70 font-medium">{label}</Text>
    </div>
  );

  const AtencionCard = ({ card }) => {
    const tone = TONE_STYLES[card.tone];
    return (
      <button
        onClick={() => navigate(card.to)}
        className={`w-full text-left rounded-lg border shadow-sm p-4 transition-all duration-200 hover:shadow-md ${tone.card}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <Text className="text-sm font-semibold text-azul-surelia truncate">{card.label}</Text>
            {card.detail && (
              <Text className={`text-xs mt-0.5 ${tone.detail}`}>{card.detail}</Text>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`text-2xl font-bold tabular-nums ${tone.value}`}>{card.value}</span>
            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
          </div>
        </div>
      </button>
    );
  };

  const QuickActionCard = ({ icon: Icon, title, description, onClick, color }) => (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-lg shadow-md border border-azul-surelia/20 p-4 hover:shadow-lg hover:border-azul-surelia/40 transition-all duration-200 text-left group"
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${color} group-hover:scale-110 transition-transform`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <Text className="font-semibold text-azul-surelia mb-1">{title}</Text>
          <Text className="text-sm text-azul-surelia/60">{description}</Text>
        </div>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const showComisiones =
    isOwner &&
    (comisionSaldos.cobrable > 0 || comisionSaldos.porExigir > 0 || comisionSaldos.adeudo > 0 || metasRows.length > 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <Heading level={1} className="text-4xl font-bold text-azul-surelia mb-2">
          {getGreeting()}, {userData?.nombre || 'Usuario'}!
        </Heading>
        <Text className="text-azul-surelia/70">
          Aquí está el resumen de tu actividad en Surelia
        </Text>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
        <KpiCard
          icon={UserGroupIcon}
          label="Clientes"
          value={kpis.clientes}
          onClick={() => navigate('/app/clientes')}
        />
        <KpiCard
          icon={DocumentCheckIcon}
          label="Contratos"
          value={kpis.contratos}
          onClick={() => navigate('/app/contratos')}
        />
        <KpiCard
          icon={DocumentTextIcon}
          label="Fianzas vigentes"
          value={kpis.fianzasVigentes}
          onClick={() => navigate('/app/fianzas')}
        />
        <KpiCard
          icon={BanknotesIcon}
          label="Prima vigente"
          value={fmtMXN(kpis.primaVigente)}
          onClick={() => navigate('/app/fianzas')}
        />
      </div>

      {/* Requiere tu atención */}
      <div className="mb-8">
        <Heading level={3} className="text-azul-surelia mb-4">
          Requiere tu atención
        </Heading>
        {atencion.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {atencion.map((card) => (
              <AtencionCard key={card.key} card={card} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-verde-lima/10 rounded-lg border border-verde-lima/30">
            <CheckCircleIcon className="w-6 h-6 text-verde-lima shrink-0" />
            <Text className="text-sm font-medium text-azul-surelia">
              Todo en orden — sin pendientes urgentes
            </Text>
          </div>
        )}
      </div>

      {/* Comisiones (owner) */}
      {showComisiones && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow-lg border border-azul-surelia/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading level={3} className="text-azul-surelia flex items-center gap-2">
                <BanknotesIcon className="w-5 h-5" />
                Comisiones
              </Heading>
              <Button outline color="surelia" onClick={() => navigate('/app/comisiones')} className="text-sm">
                Ver detalle
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-verde-lima/10 rounded-lg border border-verde-lima/30">
                <Text className="text-xs text-azul-surelia/70 mb-1">Cobrable ahora</Text>
                <Text className="text-xl font-bold text-azul-surelia tabular-nums">
                  {fmtMXN(comisionSaldos.cobrable)}
                </Text>
              </div>
              <div className="p-4 bg-beige-suave/30 rounded-lg border border-azul-surelia/20">
                <Text className="text-xs text-azul-surelia/70 mb-1">Por exigir</Text>
                <Text className="text-xl font-bold text-azul-surelia tabular-nums">
                  {fmtMXN(comisionSaldos.porExigir)}
                </Text>
              </div>
              <div className={`p-4 rounded-lg border ${comisionSaldos.adeudo > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                <Text className="text-xs text-azul-surelia/70 mb-1">Adeudo con afianzadoras</Text>
                <Text className={`text-xl font-bold tabular-nums ${comisionSaldos.adeudo > 0 ? 'text-red-600' : 'text-azul-surelia'}`}>
                  {fmtMXN(comisionSaldos.adeudo)}
                </Text>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading level={3} className="text-azul-surelia">Metas de prima {year}</Heading>
              <button
                type="button"
                onClick={() => navigate('/app/reportes?reporte=metas-afianzadora')}
                className="inline-flex items-center gap-0.5 text-xs font-medium text-azul-surelia hover:underline shrink-0"
              >
                Ver reporte
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            {metasRows.length > 0 ? (
              <div className="space-y-4">
                {metasRows.map((r) => {
                  const pct = Math.min(100, (r.vendida / r.meta) * 100);
                  return (
                    <div key={r.afianzadora}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Text className="text-sm font-medium text-azul-surelia truncate">{r.afianzadora}</Text>
                        <Text className="text-xs text-zinc-500 tabular-nums shrink-0">
                          {fmtMXN(r.vendida)} de {fmtMXN(r.meta)}
                        </Text>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-verde-lima h-2 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text className="text-sm text-zinc-500">Sin metas definidas</Text>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Actividad Reciente (bitácora) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/20 overflow-hidden">
            <div className="bg-gradient-to-r from-azul-surelia to-azul-surelia/90 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-white" />
                  <h3 className="text-white font-semibold text-lg">Actividad Reciente</h3>
                </div>
                <button
                  onClick={() => navigate('/app/bitacora')}
                  className="text-sm text-white/80 hover:text-white font-medium transition-colors"
                >
                  Ver bitácora
                </button>
              </div>
            </div>
            <div className="p-4">
              <BitacoraTimeline compact pageSize={6} filters={{ limit: 6 }} />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-azul-surelia/5 to-beige-suave/20 rounded-lg p-6 border border-azul-surelia/20 shadow-md">
            <Heading level={3} className="text-azul-surelia mb-4 flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5" />
              Acciones Rápidas
            </Heading>
            <div className="space-y-3">
              <QuickActionCard
                icon={ChartBarIcon}
                title="Pulso"
                description="Pipeline, vencimientos y endosos"
                onClick={() => navigate('/app/pulso')}
                color="bg-azul-surelia"
              />
              <QuickActionCard
                icon={UserGroupIcon}
                title="Gestionar Clientes"
                description="Ver y administrar clientes"
                onClick={() => navigate('/app/clientes')}
                color="bg-gradient-to-br from-azul-surelia to-azul-surelia/80"
              />
              <QuickActionCard
                icon={DocumentCheckIcon}
                title="Contratos y Fianzas"
                description="Revisar contratos activos"
                onClick={() => navigate('/app/contratos')}
                color="bg-cian-digital"
              />
              {isOwner && (
                <QuickActionCard
                  icon={BanknotesIcon}
                  title="Comisiones"
                  description="Saldos, metas y liquidación"
                  onClick={() => navigate('/app/comisiones')}
                  color="bg-verde-lima"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
